import { computed, shallowRef,  shallowReactive, triggerRef, ref, ShallowReactive, ComputedRef, Ref } from "vue";
import Events from "../utils/Events"
import RootElement from "./RootElement";
import DataElement, { DataElementAttributes } from "./DataElement";
import XMLRDBElement from "../xmlrdb-metadata/Element";

export default class ElementArray<T extends DataElementAttributes = DataElementAttributes> extends Events{

  #byId = computed(() => {
    const idmap = {} as Record<string, DataElement<T>>;
    for ( const dm of this.dataElements){
      idmap[dm.id.value] = dm;
    }
    return idmap;
  });

  parent: DataElement | null;
  metadata: XMLRDBElement;
  dataElements: ShallowReactive<DataElement<T>[]>;
  visibleDataElements: ComputedRef<DataElement<T>[]>;
  hasChanged: ComputedRef<boolean>;
  hasError: ComputedRef<boolean>;
  count: Ref<number|undefined>;
  columnSum: Record<string, Ref<number>> = {};
  changedDataElements: ComputedRef<Map<string, DataElement<T>>> = computed(() => {
    const changedElements = new Map<string, DataElement<T>>();
    for (const de of this.dataElements) {
      if (de.hasChanged.value) {
        changedElements.set(de.id.value, de);
      }
    }
    return changedElements;
  });


  constructor(metadata, dataElement) {
    super();
    /** @type {import("./DataElement").default} */
    this.parent = dataElement || null;
    /**
     * @type {import("vue").ShallowReactive<Array<import("./DataElement").default>>}
     */
    this.dataElements = shallowReactive([]);
    this.visibleDataElements = computed(() => this.dataElements.filter(d => !d.isDeleted.value));
    this.metadata = metadata;
    this.hasChanged = computed(() => this.dataElements.some(d => d.hasChanged.value));
    this.hasError = computed(() => this.dataElements.some(d => d.hasError.value));
    this.count = ref();
  }

  /**
   * @returns {Object<String,import("./DataElement").default>}
   */
  get byId() {
    return this.#byId.value;
  }

  path() {
    let path = [];
    if (this.parent !== null) {
      path = [...this.parent.path()];
    }
    path.push(this.metadata.id());
    return path;
  }
  
   root() {
    let pivot: DataElement | RootElement = this.parent;
    while (pivot?.parent) {
      pivot = pivot.parent;
    }
    return pivot as RootElement;
  }

  /**
    Bestehendes Element ans Ende anhaengen
   */
  addElement(dataElement : DataElement<T>) {
    this.insertElement(dataElement, this.dataElements.length);
  }

  /* Legt einen neuen Datensatz an ohne ihn in das ElementArray aufzunehmen.
    Einsatz:
    x = elemArray.createFreeElement();
    // ... Setze Werte in X
    x.$.MY_ATTRIBUTE=815;
    elemArray.insertElement(x,42)
  */
  createFreeElement() {
    let dataElement = this.metadata.newDataElement(this.parent, true) as DataElement<T>;
    return dataElement;
  }

  // Neues Element anlegen und am Ende des ElementArray anhaengen.
  newElement() {
    let dataElement = this.createFreeElement() as DataElement<T>;
    this.insertElement(dataElement, this.dataElements.length);
    return dataElement;
  }

  // Neues Element anlegen und an angegebener Position des ElementArray einhaengen.
  // Liefert neues Element zurueck
  insertElement(dataElement: DataElement<T>, position?: number) {
    if (position === undefined) position = this.dataElements.length;
    dataElement.nr = this.dataElements.length;
    dataElement.elementArray = this;
    dataElement.parent = this.parent;
    this.dataElements.splice(position, 0, dataElement);
    this.dispatch("insert", dataElement);
  }

  // Neues Element anlegen und Am Anfang des ElementArray einhaengen.
  // Liefert neues Element zurueck
  insertBefore() {
    let dataElement = this.createFreeElement() as DataElement<T>;
    this.insertElement(dataElement, 0);
    return dataElement;
  }

  // Neues Element anlegen und am Ende des ElementArray anhaengen.
  // Liefert neues Element zurueck
  insertAfter() {
    return this.newElement() as DataElement<T>;
  }

  removeElement(dataElement) {
    let index = this.dataElements.indexOf(dataElement);
    if (index !== -1) {
      this.dataElements.splice(index, 1);
      this.dispatch("remove", dataElement);
      // triggerRef(this.dataElements)
    }
  }

  delete(dataElement : DataElement<T>) {
    if (dataElement.isNew.value) {
      this.removeElement(dataElement);
    }
    else {
      let index = this.dataElements.indexOf(dataElement);
      if (index === -1) {
        console.log("not found", dataElement)
        throw new Error("DataElement ist nicht im Array enthalten");
      }
      dataElement.isDeleted.value = true;
    }
    this.dispatch("delete", dataElement);
  }

  deleteAll() {
    for (let de of this.dataElements) {
      this.delete(de);
    }
  }

  /**
   * level == 0 liefert this
   * level == 1 liefert direkt parent DataElement
   * ...
   * @returns {import("./DataElement").default}
   */
  parentByLevel(level) {
    let pivot: DataElement | RootElement | ElementArray= this;
    while (pivot.parent && level-- > 0) {
      pivot = pivot.parent;
    }
    return pivot as RootElement | DataElement;
  }
    
  update() {
    this.dispatch("update", this.dataElements);
  }

  /**
   * 
   * @param {ElementArray} elementArray 
   */
  merge(elementArray) {
    // console.assert(
    //   elementArray.dataElements.length >= this.dataElements.length,
    //   "Umrsprungsarray und bearbeitete Kopie haben eine unterschiedliche Länge",
    //   elementArray.dataElements.length, this.dataElements.length, this.metadata.name());

    let i = 0;
    for (; i < this.dataElements.length; i++){
      this.dataElements[i].merge(elementArray.dataElements[i]);
    }

    for (; i < elementArray.dataElements.length; i++){
      const newElement = this.newElement();
      newElement.merge(elementArray.dataElements[i]);
    }
  }


}